import React from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

interface StripePaymentFormProps {
    saveCard: boolean;
    onSaveCardChange: (checked: boolean) => void;
    onValidityChange: (isValid: boolean) => void;
}

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#32325d',
            fontFamily: 'Arial, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
        },
    },
    hidePostalCode: true,
};

const StripePaymentForm = ({ saveCard, onSaveCardChange, onValidityChange }: StripePaymentFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardComplete, setCardComplete] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    // Remove onValidityChange from dependencies to prevent infinite loop
    React.useEffect(() => {
        onValidityChange(cardComplete && !error);
    }, [cardComplete, error]);

    const handleCardChange = (event: any) => {
        setError(event.error ? event.error.message : '');
        setCardComplete(event.complete);
    };

    return (
        <div className="space-y-6">
            <div>
                <Label htmlFor="card-element" className="text-base font-medium mb-2 block">
                    Card Details <span className="text-red-500">*</span>
                </Label>
                <div className="p-4 border rounded-md bg-white">
                    <CardElement id="card-element" options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
                </div>
                {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
            </div>

            <div className="flex items-center justify-between">
                <div>
                    <div className="font-medium">Store card for future billing?</div>
                    <div className="text-sm text-gray-500">
                        If you need more info, please check budget planning
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Switch
                        id="save-card"
                        checked={saveCard}
                        onCheckedChange={onSaveCardChange}
                    />
                    <Label
                        htmlFor="save-card"
                        className="text-sm text-gray-600 cursor-pointer"
                    >
                        Store Card
                    </Label>
                </div>
            </div>
        </div>
    );
};

export default StripePaymentForm;
